import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { faFileLines, faPuzzlePiece, faPenNib, faCopy, faRobot, faGraduationCap, faCloudArrowUp, faChartColumn, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserGraduate, faUniversity, faBookOpen, faBuilding, faSchool, faGlobe, faUser, } from "@fortawesome/free-solid-svg-icons";

/* ─── data ─────────────────────────────────────────────── */
const detectionTypes = [
  {
    icon: faFileLines,
    title: "Verbatim copying",
    desc: "Exact and near-exact text matches across published literature",
  },
  {
    icon: faPuzzlePiece,
    title: "Mosaic plagiarism",
    desc: "Patchwork reuse stitched from multiple sources with light rewording",
  },
  {
    icon: faPenNib,
    title: "Paraphrase detection",
    desc: "Semantically similar content reworded to avoid detection",
  },
  {
    icon: faCopy,
    title: "Self-plagiarism",
    desc: "Duplicate publication across an author's own previous work",
  },
  {
    icon: faRobot,
    title: "AI-generated content",
    desc: "Identifies AI-written text within submitted manuscripts",
  },
  {
    icon: faGraduationCap,
    title: "LMS integration",
    desc: "Connects with Moodle and Canvas for institutional deployments",
  },
];

const solutions = [
  {
    icon: faUserGraduate,
    name: "Researchers",
    desc: "Check before submission. Know where you stand.",
    bg: "#dbeafe",
    to: "/researchers",
  },
  {
    icon: faUniversity,
    name: "Institutions",
    desc: "Scalable checks with LMS and bulk uploads.",
    bg: "#ede9fe",
    to: "/institutions",
  },
  {
    icon: faBookOpen,
    name: "Publishers",
    desc: "Validate via REST API for editorial workflows.",
    bg: "#ffe4e6",
    to: "/publishers",
  },
  {
    icon: faBuilding,
    name: "Corporates",
    desc: "Originality and compliance for business docs.",
    bg: "#dcfce7",
    to: "/corporate",
  },
  {
    icon: faSchool,
    name: "Schools",
    desc: "Easy tools to promote honest academic learning.",
    bg: "#fef9c3",
    to: "/institutions",
  },
  {
    icon: faGlobe,
    name: "Consortiums",
    desc: "Standardize integrity across multiple institutions.",
    bg: "#f0fdf4",
    to: "/institutions",
  },
  {
    icon: faUser,
    name: "Individuals",
    desc: "On-demand analysis for content originality.",
    bg: "#fef3c7",
    to: "/individuals",
  },
];

const reportSources = [
  { pct: '8%', title: 'Climate change impacts on agricultural productivity in dryland regions', journal: 'Nature Climate Change · 2021', color: 'text-red-600 bg-red-50' },
  { pct: '5%', title: 'Food security under climate variability in South and Southeast Asia', journal: 'Science · 2019', color: 'text-amber-600 bg-amber-50' },
  { pct: '3%', title: 'Water stress and crop failure risks under global warming scenarios', journal: 'PNAS · 2020', color: 'text-green-600 bg-green-50' },
  { pct: '2%', title: 'Agricultural adaptation strategies under climatic uncertainty', journal: 'Global Food Security · 2022', color: 'text-green-600 bg-green-50' },
];

const reportTabs = [
  { id: 'overview', label: 'Overall similarity score', num: '01' },
  { id: 'sources', label: 'Matched sources', num: '02' },
  { id: 'ai', label: 'AI content breakdown', num: '03' },
  { id: 'highlight', label: 'Sentence-level highlights', num: '04' },
];
// const howItWorks = [
//   {
//     step: "01",
//     icon: faCloudArrowUp,
//     title: "Submit",
//     desc: "Drag and drop your document or manuscript.",
//   },
//   {
//     step: "02",
//     icon: faMagnifyingGlass,
//     title: "Analysis",
//     desc: "Manna Plagiarism screens your work for content integrity, AI generated content, similarity checks, plagiarism.",
//   },
//   {
//     step: "03",
//     icon: faChartColumn,
//     title: "Report",
//     desc: "Get a detailed report on the work to make a clear plan on how to proceed.",
//   },
// ];
// const whyCards = [
//   { emoji: '📅', title: 'Daily corpus updates', desc: 'Our reference corpus grows every day as new research is published. What we check against today is broader than yesterday.', tag: 'Always current', tagBg: '#eff6ff', tagTxt: '#1e40af' },
//   { emoji: '🏢', title: 'Built by publishers', desc: 'Over a decade building manuscript management for the publishing industry. We understand editorial integrity not just theory.', tag: 'Domain expertise', tagBg: '#f5f3ff', tagTxt: '#5b21b6' },
//   { emoji: '🔒', title: 'GDPR compliant', desc: 'Your manuscript data is handled with strict privacy controls. Right to access, manage, and request deletion no exceptions.', tag: 'Privacy first', tagBg: '#ecfdf5', tagTxt: '#065f46' },
//   { emoji: '🔗', title: 'API-first integration', desc: 'Publisher and enterprise teams can embed checks directly into existing editorial workflows via our REST API no context switching.', tag: 'REST API ready', tagBg: '#fff7ed', tagTxt: '#92400e' },
//   { emoji: '⚡', title: 'Results in minutes', desc: 'Full reports similarity score, source list, AI breakdown, sentence highlights delivered in minutes. Start with 3 free checks.', tag: 'Fast turnaround', tagBg: '#fdf4ff', tagTxt: '#701a75' },
// ];

const testimonials = [
  { quote: '"The depth of AI detection is unmatched. It gave us confidence to approve manuscripts that showed subtle signs of synthetic generation."', name: 'Dr. Tamil Selvan', role: 'Editorial Director', initials: 'TS', bg: '#1e40af' },
  { quote: '"Switching to MannaPlag reduced our administrative overhead significantly. The LMS integration is seamless for our entire faculty."', name: 'Dr. Clement Arokisamy', role: 'Principal, Meston College', initials: 'CA', bg: '#065f46' },
  { quote: '"As a journal publisher, trust is everything. MannaPlag gives us the objectivity we need to maintain our editorial standards."', name: 'Dr. Alfi David', role: 'Vice Chancellor, Arunachal University', initials: 'AD', bg: '#003c6c' },
];

const clients = [
  {
    name: 'i-Manager Publications',
    sub: 'Commercial Publisher · 50+ Journals',
    logo: `${process.env.PUBLIC_URL}/clients/imanager.png`,
  },
  {
    name: 'JIDAT',
    sub: 'Dental Association Journal',
    logo: `${process.env.PUBLIC_URL}/clients/jidat.png`,
  },
  {
    name: 'ISDA Journal',
    sub: 'Peer-Reviewed Research Journal',
    logo: `${process.env.PUBLIC_URL}/clients/isda-journal.png`,
  },
  {
    name: 'Integrated Intelligent Research (IIR)',
    sub: 'Research & Scholarly Publisher',
    logo: `${process.env.PUBLIC_URL}/clients/iir.png`,
  },
  {
    name: 'B-Digest Publications',
    sub: 'Scholarly Journal Press',
    logo: `${process.env.PUBLIC_URL}/clients/b-digest.png`,
  },
  {
    name: "Women's Christian College",
    sub: 'Autonomous College & Research Press',
    logo: `${process.env.PUBLIC_URL}/clients/wcc-nagercoil.png`,
  },
  {
    name: 'The Dale View College of Pharmacy',
    sub: 'Pharmacy & Biomedical Research Centre',
    logo: `${process.env.PUBLIC_URL}/clients/the-dale-view.png`,
  },
  {
    name: 'Karpathu Tamil Arakattalai',
    sub: 'Educational & Cultural Trust',
    logo: `${process.env.PUBLIC_URL}/clients/karpathu-tamil.png`,
  },
];

/* ─── Report tab panels ─────────────────────────────────── */

/* ─── Report tab panels ─────────────────────────────────── */
function ReportOverview() {
  return (
    <div className="p-6 tab-panel-enter">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <span className="inline-block text-[9px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded mb-1.5">Sample Report · Demo Data</span>
          <p className="text-[13px] font-bold text-gray-900 leading-snug">Effects of climate variability on agricultural productivity in South Asia</p>
          <p className="text-[11px] text-gray-400 mt-1 font-medium">Submitted Jun 13, 2026 · 8,420 words · PDF</p>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-[32px] font-black text-red-500 leading-none">18%</p>
          <p className="text-[10px] text-gray-400 font-medium mt-0.5">overall similarity</p>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-5">
        {[['12%', '#ef4444', 'verbatim'], ['4%', '#f59e0b', 'mosaic'], ['2%', '#8b5cf6', 'AI generated'], ['0%', '#9ca3af', 'self-plagiarism']].map(([v, c, l]) => (
          <div key={l} className="bg-gray-50 rounded-md p-3 text-center border border-gray-100">
            <p className="text-[16px] font-bold" style={{ color: c }}>{v}</p>
            <p className="text-[10px] text-gray-400 mt-1 font-medium">{l}</p>
          </div>
        ))}
      </div>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Similarity by section</p>
      {[['Introduction', '28%', 28, '#ef4444'], ['Methodology', '9%', 9, '#10b981'], ['Results', '5%', 5, '#10b981'], ['Discussion', '22%', 22, '#f59e0b']].map(([sec, pct, w, c]) => (
        <div key={sec as string} className="flex items-center gap-3 mb-2">
          <span className="text-[11px] font-medium text-gray-500 w-24 flex-shrink-0">{sec}</span>
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${w}%`, background: c as string }} />
          </div>
          <span className="text-[11px] font-bold w-8 text-right" style={{ color: c as string }}>{pct}</span>
        </div>
      ))}
    </div>
  );
}

function ReportSources() {
  return (
    <div className="p-6 tab-panel-enter">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Top matching sources</p>
      {reportSources.map((s, i) => (
        <div key={s.title} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-md mb-2 border border-gray-100">
          <div className="min-w-0 flex items-center gap-2.5">
            <span className="text-[10px] font-bold text-white w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: i === 0 ? '#ef4444' : i === 1 ? '#f59e0b' : '#10b981' }}>{i + 1}</span>
            <div>
              <p className="text-[12px] font-semibold text-gray-800 truncate">{s.title}</p>
              <p className="text-[10px] text-gray-400 mt-0.5 font-medium">{s.journal}</p>
            </div>
          </div>
          <span className={`text-[12px] font-bold px-2 py-1 rounded-md flex-shrink-0 ${s.color}`}>{s.pct}</span>
        </div>
      ))}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-md">
        <p className="text-[11px] text-blue-700 font-medium">Checked against <strong>millions</strong> of published papers, preprints, and theses</p>
      </div>
    </div>
  );
}

function ReportAI() {
  return (
    <div className="p-6 tab-panel-enter">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">AI content analysis</p>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[['2%', '#8b5cf6', 'AI-generated'], ['98%', '#10b981', 'Human-written'], ['91%', '#374151', 'Avg. confidence']].map(([v, c, l]) => (
          <div key={l} className="bg-gray-50 rounded-md p-3 text-center border border-gray-100">
            <p className="text-[18px] font-bold" style={{ color: c }}>{v}</p>
            <p className="text-[10px] text-gray-400 mt-1 font-medium">{l}</p>
          </div>
        ))}
      </div>
      {[['Introduction (¶2–¶4)', 94, '#8b5cf6'], ['Discussion (¶11)', 78, '#8b5cf6'], ['Methodology (all)', 100, '#10b981']].map(([name, pct, c]) => (
        <div key={name as string} className="mb-4">
          <div className="flex justify-between mb-1.5">
            <span className="text-[12px] font-semibold text-gray-700">{name}</span>
            <span className="text-[12px] font-bold" style={{ color: c as string }}>{pct === 100 ? 'Human' : `${pct}%`}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: c as string }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportHighlight() {
  return (
    <div className="p-6 tab-panel-enter">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Sentence-level highlights</p>
      <div className="flex gap-4 mb-4">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-100 border border-red-300" /><span className="text-[11px] font-medium text-gray-500">Verbatim match</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-purple-100 border border-purple-300" /><span className="text-[11px] font-medium text-gray-500">AI-generated</span></div>
      </div>
      <p className="text-[13px] text-gray-600 leading-8">
        Climate change is{' '}
        <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded-md font-medium">increasingly recognized as one of the most significant threats to global food security</span>
        , particularly in tropical and subtropical regions.{' '}
        <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-md font-medium">Agricultural systems in South Asia face compounding stressors including erratic monsoon patterns, rising temperatures, and soil degradation.</span>
        {' '}Our dataset covered 1,240 farm households across three districts.
      </p>
      <div className="flex flex-wrap gap-2 mt-4">
        <span className="text-[10px] bg-red-50 text-red-700 px-2.5 py-1.5 rounded-md font-semibold border border-red-100">Nature Climate Change, 2021, 8%</span>
        <span className="text-[10px] bg-purple-50 text-purple-700 px-2.5 py-1.5 rounded-md font-semibold border border-purple-100">AI detected 94% confidence</span>
      </div>
    </div>
  );
}

const reportPanels: Record<string, React.ReactElement> = {
  overview: <ReportOverview />,
  sources: <ReportSources />,
  ai: <ReportAI />,
  highlight: <ReportHighlight />,
};

const homeFaqs = [
  {
    q: 'Is MannaPlag a reliable Turnitin and iThenticate alternative?',
    a: 'Yes. MannaPlag is specifically designed as an accessible, high-precision academic alternative to Turnitin and iThenticate. It compares manuscripts against 50M+ scholarly articles, institutional repositories, and open-access journals. Individual researchers and institutions can run instant checks without requiring long-term enterprise contracts.',
  },
  {
    q: 'How does MannaPlag detect AI-generated content?',
    a: 'MannaPlag uses multi-model perplexity and burstiness analysis to detect AI-generated text produced by ChatGPT (GPT-4o), Claude, Gemini, and academic paraphrasers. It flags synthetic sentences with confidence scores, differentiating between human-written, AI-generated, and human-edited mixed content.',
  },
  {
    q: 'Does MannaPlag store or repository-lock my uploaded manuscript?',
    a: 'No. Unlike traditional tools that automatically deposit submitted files into a central database—risking self-plagiarism flags on final university submission—MannaPlag never stores your unpublished papers into a public searchable repository. Your intellectual property remains 100% private.',
  },
  {
    q: 'How is the overall similarity percentage calculated?',
    a: 'The similarity score represents the proportion of text that matches indexed literature. MannaPlag breaks down matches into verbatim copying, mosaic (patchwork) reuse, paraphrased text, and cited material. You can filter bibliographies and quoted content with custom thresholds.',
  },
  {
    q: 'Can institutions and publishers integrate MannaPlag via LMS or API?',
    a: 'Yes. MannaPlag supports Moodle, Canvas, and Open Journal Systems (OJS) integrations alongside a comprehensive REST API for automated manuscript screening inside publisher editorial systems.',
  },
  {
    q: 'Which file formats and languages are supported?',
    a: 'MannaPlag supports PDF, DOCX, DOC, and TXT files up to 50MB. The engine accurately screens manuscripts in English and multiple international academic languages, providing detailed side-by-side source comparisons with direct links.',
  },
];

/* ─── Page ───────────────────────────────────────────────── */
export default function Home() {
  const [activeTab, setActiveTab] = useState('overview');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div>
      <SEO
        title="MannaPlag: Plagiarism Checker & AI Detector | Turnitin Alternative"
        description="Academic plagiarism checker and AI detector with sentence-level similarity reports. Trusted by journals and universities. Try 3 free checks."
        canonicalPath="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'SoftwareApplication',
              name: 'MannaPlag',
              applicationCategory: 'EducationalApplication',
              operatingSystem: 'Web',
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
              },
              description:
                'Academic plagiarism checker and AI detection platform for researchers, universities, and journal publishers.',
            },
            {
              '@type': 'Organization',
              name: 'Manna Analytics Private Limited',
              url: 'https://mannanalytics.com',
              logo: 'https://mannaplag.mannaanalytics.com/og-image.png',
              sameAs: [
                'https://www.linkedin.com/company/manna-analytics/',
                'https://www.instagram.com/manna_analytics/',
                'https://www.youtube.com/@MannaCloud',
              ],
            },
            {
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'Is MannaPlag a reliable Turnitin and iThenticate alternative?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes. MannaPlag is specifically engineered as an accessible, high-precision academic alternative to Turnitin and iThenticate, comparing manuscripts against 50M+ scholarly articles, institutional repositories, and open-access journals without requiring enterprise contracts.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'How does MannaPlag detect AI-generated content?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'MannaPlag analyzes perplexity, burstiness, and sentence-level structural patterns to identify text generated by ChatGPT (GPT-4o), Claude, Gemini, and academic paraphrasers with high accuracy.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'Does MannaPlag store or repository-lock my uploaded manuscript?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'No. Unlike traditional checkers that deposit papers into a central database—risking self-plagiarism flags on final university submission—MannaPlag never stores your unpublished manuscripts into a public repository.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'How is the similarity percentage calculated?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'The overall similarity score indicates the proportion of text matching indexed literature. The report categorizes verbatim matches, mosaic reuse, and AI content, with options to filter bibliographies and quoted passages.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'Can institutions and publishers integrate MannaPlag via LMS or API?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes. MannaPlag provides native integrations for Moodle, Canvas, and Open Journal Systems (OJS) alongside a robust REST API for automated manuscript screening in editorial pipelines.',
                  },
                },
              ],
            },
          ],
        }}
      />
      {/* ── HERO ── */}
      <section className="bg-hero relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-[60px] font-black text-white leading-[1.12] mb-6 max-w-4xl mx-auto tracking-tight">
              Academic <span className="text-blue-300">Plagiarism Checker</span> &amp; AI Detector for Researchers, Publishers &amp; Universities
            </h1>
            <p className="text-[15px] text-white/70 max-w-2xl mx-auto mb-6 leading-relaxed font-medium">
              Ensure the originality and uniqueness of every word you write with MannaPlag in the fast-expanding world of academics and literature. Engineered specifically for research scholars, academic institutions, and publishers.
            </p>
            <div className="flex flex-wrap gap-2.5 justify-center mb-8">
              {['Works with accuracy', 'Fits with your workflow', 'Quick results', 'Affordable price'].map(t => (
                <span key={t} className="text-[11px] font-semibold text-white/80 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full">{t} </span>
              ))}
            </div>
            <div className="flex gap-3 justify-center">
              <Link to="/contact" className="px-6 py-3 bg-blue text-white text-[14px] font-bold rounded-md transition-colors hover:bg-white hover:text-gray-900 shadow-lg">
                Request a demo
              </Link>
              <Link to="/register" className="px-6 py-3 border bg-white border-white/25 text-gray-900 text-[14px] font-semibold rounded-md hover:bg-white/10 hover:text-white transition-colors">
                Get started free
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── SUBMIT · ANALYSIS · REPORT ── */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-14">
            <h2 className="text-[36px] sm:text-[42px] font-black text-gray-900 mb-4 tracking-tight leading-tight">
              Submit. Analyse. Report. All in One Scan.
            </h2>
            <p className="text-[15px] text-gray-500 max-w-2xl mx-auto leading-relaxed font-medium">
              If you are looking for a plagiarism checker like Turnitin, Manna Plag provides accurate results,
              professional reports, and AI detection, all in one platform.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* Card 1 — Submit */}
            <div className="rounded-lg p-8 flex flex-col justify-between min-h-[300px] relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #003c6c 0%, #0369a1 100%)' }}>
              <div>
                <p className="text-[11px] font-bold text-blue-200 uppercase tracking-widest mb-3">Step 01</p>
                <h3 className="text-[26px] font-black text-white leading-tight mb-3">
                  Submit your manuscript
                </h3>
                <p className="text-[13px] text-white/70 leading-relaxed">
                  Drag and drop your PDF or DOCX file. Supports manuscripts up to 50MB. Results start within seconds.
                </p>
              </div>
              <div className="mt-8 flex items-center justify-between">
                <FontAwesomeIcon icon={faCloudArrowUp} className="text-white/20 text-[80px] absolute bottom-4 right-4" />
                <span className="relative z-10 inline-flex items-center gap-1.5 bg-white/15 text-white text-[12px] font-bold px-4 py-2 rounded-full">
                  PDF · DOCX · TXT
                </span>
              </div>
            </div>

            {/* Card 2 — Analysis */}
            <div className="rounded-lg p-8 flex flex-col justify-between min-h-[300px] relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)' }}>
              <div>
                <p className="text-[11px] font-bold text-blue-200 uppercase tracking-widest mb-3">Step 02</p>
                <h3 className="text-[26px] font-black text-white leading-tight mb-3">
                  AI-powered analysis
                </h3>
                <p className="text-[13px] text-white/70 leading-relaxed">
                  Manna Plag screens your work for verbatim matches, mosaic plagiarism, self-plagiarism, and AI-generated content across 50M+ scholarly documents.
                </p>
              </div>
              <div className="mt-8">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="text-white/20 text-[80px] absolute bottom-4 right-4" />
                <span className="relative z-10 inline-flex items-center gap-1.5 bg-white/15 text-white text-[12px] font-bold px-4 py-2 rounded-full">
                  Results in minutes
                </span>
              </div>
            </div>

            {/* Card 3 — Report */}
            <div className="rounded-lg p-8 flex flex-col justify-between min-h-[300px] relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)' }}>
              <div>
                <p className="text-[11px] font-bold text-blue-200 uppercase tracking-widest mb-3">Step 03</p>
                <h3 className="text-[26px] font-black text-white leading-tight mb-3">
                  Detailed integrity report
                </h3>
                <p className="text-[13px] text-white/70 leading-relaxed">
                  Get a source-by-source similarity breakdown, AI content score, highlighted sentences, and a downloadable PDF report you can act on immediately.
                </p>
              </div>
              <div className="mt-8">
                <FontAwesomeIcon icon={faChartColumn} className="text-white/20 text-[80px] absolute bottom-4 right-4" />
                <span className="relative z-10 inline-flex items-center gap-1.5 bg-white/15 text-white text-[12px] font-bold px-4 py-2 rounded-full">
                  PDF download included
                </span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── REPORT PREVIEW ── */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
          <div className="text-center mb-10">
            <h2 className="text-[26px] font-black text-gray-900 mb-2 tracking-tight">See exactly what you get</h2>
            <p className="text-[14px] text-gray-500 max-w-md mx-auto leading-relaxed font-medium">
              A detailed similarity report broken down so you know exactly what to act on
            </p>
          </div>

          <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between gap-6 px-8 py-7 border-b border-gray-100 bg-gray-50/60">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">Sample Report · Demo Data</span>
                  <span className="text-[11px] text-gray-400 font-semibold">manuscript.pdf</span>
                </div>
                <p className="text-[15px] font-bold text-gray-900 leading-snug">
                  Effects of climate variability on agricultural productivity in South Asia
                </p>
                <p className="text-[12px] text-gray-400 mt-1.5 font-medium">Submitted Jun 13, 2026 · 8,420 words · 34 pages</p>
              </div>
              <div className="flex-shrink-0 text-center">
                <div className="w-20 h-20 rounded-lg bg-red-500 flex flex-col items-center justify-center shadow-md">
                  <span className="text-[28px] font-black text-white leading-none">18%</span>
                  <span className="text-[9px] text-red-100 mt-0.5 font-semibold">similarity</span>
                </div>
              </div>
            </div>

            {/* Score breakdown */}
            <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
              {[
                { val: '12%', color: '#ef4444', label: 'Verbatim', bg: '#fef2f2' },
                { val: '4%', color: '#f59e0b', label: 'Mosaic', bg: '#fffbeb' },
                { val: '2%', color: '#8b5cf6', label: 'AI generated', bg: '#faf5ff' },
                { val: '0%', color: '#9ca3af', label: 'Self-plagiarism', bg: '#f9fafb' },
              ].map(({ val, color, label, bg }) => (
                <div key={label} className="py-6 text-center" style={{ background: bg }}>
                  <p className="text-[26px] font-black" style={{ color }}>{val}</p>
                  <p className="text-[11px] text-gray-500 mt-1.5 font-semibold">{label}</p>
                </div>
              ))}
            </div>

            {/* Sources */}
            <div className="px-8 py-6">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Top matching sources</p>
              <div className="flex flex-col gap-2.5">
                {reportSources.map((s, i) => (
                  <div key={s.title} className={`report-source-row flex items-center justify-between gap-4 px-5 py-4 rounded-md border cursor-pointer transition-all ${i === 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                    }`}>
                    <div className="min-w-0 flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-black text-white ${i === 0 ? 'bg-red-400' : i === 1 ? 'bg-amber-400' : 'bg-green-500'
                        }`}>{i + 1}</div>
                      <div>
                        <p className="text-[13px] font-semibold text-gray-800 leading-snug">{s.title}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5 font-medium">{s.journal}</p>
                      </div>
                    </div>
                    <span className={`text-[13px] font-bold px-3 py-1.5 rounded-md flex-shrink-0 ${s.color}`}>{s.pct}</span>
                  </div>
                ))}
              </div>
              {/* <div className="mt-5 flex items-center justify-between pt-4 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 font-medium">Checked against <strong className="text-gray-700">millions</strong> of published papers</p>
                <span className="text-[12px] font-semibold text-blue-600 cursor-pointer hover:underline">View full report →</span>
              </div> */}
            </div>
          </div>
        </div>
      </section>



      {/* ── AI DETECTION ── */}
      <section className="bg-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-[28px] font-black text-white mb-4 leading-tight tracking-tight">
                AI Content Detection for<br />Professionals &amp; Institutions
              </h2>
              <p className="text-[14px] text-white/60 leading-relaxed mb-8 font-medium">
                Analyze content to identify AI-generated text and similarity patterns. Enables publishers, educators, and institutions to verify authorship with confidence.
              </p>
              <div className="flex flex-col gap-4">
                {[
                  'Detects AI-generated text at sentence level',
                  'Highlights mixed human/AI content',
                  'Works across all major AI models',
                  'Publisher and institution grade accuracy',
                ].map(t => (
                  <div key={t} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-white border  border-purple-400/40 flex items-center justify-center flex-shrink-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-hero animate-pulse" />
                    </div>
                    <span className="text-[13px] text-white/80 font-semibold">{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Viz - matches reference image */}
            <div className="bg-white border border-white/10 rounded-lg p-6">
              <p className="text-[10px] font-black text-gray/900 uppercase tracking-widest mb-6">Content analysis preview</p>

              <div className="flex flex-col gap-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[11px] text-gray/900 font-medium">Paragraph 2–4 · Introduction</span>
                    <span className="text-[11px] font-bold text-purple-300">87% AI</span>
                  </div>
                  <div className="h-2.5 bg-white/10 rounded-full overflow-hidden mb-1.5">
                    <div className="h-full rounded-full ai-bar" style={{ width: '87%', background: 'rgba(139,92,246,0.75)' }} />
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div className="h-full rounded-full ai-bar" style={{ width: '72%', background: 'rgba(139,92,246,0.5)', animationDelay: '0.15s' }} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px]">⚠</span>
                    <span className="text-[11px] font-semibold text-purple-300">AI-generated 87% confidence</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[11px] text-gray/900 font-medium">Paragraph 7–9 · Methodology</span>
                    <span className="text-[11px] font-bold text-teal-600">
                      Human written
                    </span>
                  </div>
                  <div className="h-2.5 bg-white/10 rounded-full overflow-hidden mb-1.5">
                    <div className="h-full rounded-full" style={{ width: '92%', background: '#003c6c' }} />
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div className="h-full rounded-full" style={{ width: '78%', background: '#003c6c' }} />
                  </div>
                  <span className="text-[11px] font-semibold text-teal-600">
                    Human-written
                  </span>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[11px] text-gray/900 font-medium">Paragraph 11 · Discussion</span>
                    <span className="text-[11px] font-bold text-red-300">Verbatim match</span>
                  </div>
                  <div className="h-2.5 bg-white/10 rounded-full overflow-hidden mb-1.5">
                    <div className="h-full rounded-full ai-bar" style={{ width: '78%', background: 'rgba(239,68,68,0.65)', animationDelay: '0.3s' }} />
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div className="h-full rounded-full ai-bar" style={{ width: '62%', background: 'rgba(239,68,68,0.4)', animationDelay: '0.45s' }} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px]">⚠</span>
                    <span className="text-[11px] font-semibold text-red-300">Verbatim match Nature, 2022</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-5 mt-6 pt-5 border-t border-gray-100 flex-wrap">
                {[
                  { color: 'rgba(139,92,246,0.75)', label: 'AI content' },
                  { color: '#003c6c', label: 'Human-written' },
                  { color: 'rgba(239,68,68,0.65)', label: 'Plagiarism' },
                ].map(({ color, label }) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm border border-gray-200" style={{ background: color }} />
                    <span className="text-[11px] text-gray-500 font-semibold">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT WE DETECT ── */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-4">
          <h2 className="text-[24px] font-black text-gray-900 mb-2 tracking-tight">What MannaPlag detects</h2>
          <p className="text-[14px] text-gray-500 mb-8 font-medium">A complete picture of your manuscript's originality</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {detectionTypes.map((d) => (
              <div
                key={d.title}
                className="bg-white border border-gray-100 rounded-md p-5 flex gap-4 items-start hover:border-gray-200 hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <FontAwesomeIcon
                    icon={d.icon}
                    className="text-blue-600 text-lg"
                  />
                </div>

                <div>
                  <p className="text-[13px] font-bold text-gray-900 mb-1">
                    {d.title}
                  </p>
                  <p className="text-[12px] text-gray-500 leading-relaxed font-medium">
                    {d.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT? HOW? WHY MANNA PLAG? ── */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
          <div className="text-center mb-10">
            <h2 className="text-[24px] font-black text-gray-900 mb-2 tracking-tight">What? How? and Why MannaPlag?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div className="bg-gray-50 border border-gray-100 rounded-md p-6">
              <p className="text-[13px] font-bold text-blue-600 uppercase tracking-widest mb-2">What you get</p>
              <p className="text-[13px] text-gray-600 leading-relaxed font-medium">A detailed similarity report that shows exactly what to act on and how to proceed with your submission.</p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-md p-6">
              <p className="text-[13px] font-bold text-blue-600 uppercase tracking-widest mb-2">How it works</p>
              <p className="text-[13px] text-gray-600 leading-relaxed font-medium">Submit your manuscript and check against AI generated text and similarity patterns to make your script a step forward.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-gray-100 rounded-md p-6">
              <p className="text-[13px] font-bold text-gray-900 mb-3">Works with</p>
              <ul className="flex flex-col gap-2.5">
                {['Almost all major AI', 'Utmost accuracy', 'From sentence level', 'AI and human mixed content'].map(t => (
                  <li key={t} className="flex items-center gap-2.5 text-[12px] text-gray-600 font-medium">
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-gray-100 rounded-md p-6">
              <p className="text-[13px] font-bold text-gray-900 mb-3">Why choose MannaPlag</p>
              <ul className="flex flex-col gap-2.5">
                {['End-to-end encryption across your content', 'Accuracy', 'Live detailed report', 'Works within minutes', 'Source links in reports'].map(t => (
                  <li key={t} className="flex items-center gap-2.5 text-[12px] text-gray-600 font-medium">
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOLUTIONS ── */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-10">
            <h2 className="text-[24px] font-black text-gray-900 mb-2 tracking-tight">End-to-end content integrity across the writing lifecycle</h2>
            <p className="text-[14px] text-gray-500 font-medium">Tailored for every stage of research, review, and publication</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {solutions.map(s => (
              <Link key={s.name} to={s.to} className="bg-white border border-gray-100 rounded-md p-4 hover:border-gray-200 hover:shadow-sm transition-all group">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  style={{ background: s.bg }}
                >
                  <FontAwesomeIcon
                    icon={s.icon}
                    className="text-blue-600 text-lg"
                  />
                </div>
                <p className="text-[13px] font-bold text-gray-900 mb-1.5">{s.name}</p>
                <p className="text-[11px] text-gray-500 leading-relaxed font-medium">{s.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── SPECIALLY WEAVED ONLY FOR YOU ── */}
      <section className="bg-[#f0f7ff] border-b border-blue-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="text-[24px] font-black text-gray-900 mb-2 tracking-tight">Engineered specifically for your workflow</h2>
            <p className="text-[14px] text-gray-500 font-medium">If you are a student, research scholar or a publisher</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-gray-100 rounded-md p-6">
              <p className="text-[13px] font-bold text-green-700 mb-4">This is for you if you</p>
              <ul className="flex flex-col gap-3">
                {['Need a plagiarism check with utmost accuracy', 'Value integrity in your work', 'Need originality and uniqueness', 'Need full confidentiality', 'Want your work to stand out from others'].map(t => (
                  <li key={t} className="flex items-start gap-2.5 text-[12px] text-gray-600 font-medium">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-gray-100 rounded-md p-6">
              <p className="text-[13px] font-bold text-red-600 mb-4">This is not for</p>
              <ul className="flex flex-col gap-3">
                {['Copy writers', 'If you completely depend on AI and copy-pasted content', 'And prefer less accuracy'].map(t => (
                  <li key={t} className="flex items-start gap-2.5 text-[12px] text-gray-600 font-medium">
                    <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── REPORT WALKTHROUGH ── */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-[24px] font-black text-gray-900 mb-2 tracking-tight">What a MannaPlag report actually looks like</h2>
          <p className="text-[14px] text-gray-500 mb-10 font-medium">Clear, detailed, actionable click through the sections of a real report</p>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="md:col-span-2 flex flex-col gap-2">
              {reportTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`text-left p-4 rounded-md border transition-all ${activeTab === tab.id
                    ? 'bg-white border-gray-200 shadow-sm'
                    : 'bg-transparent border-transparent hover:bg-white/50'
                    }`}
                >
                  <p className="text-[10px] font-bold text-blue-500 mb-1">{tab.num}</p>
                  <p className="text-[13px] font-bold text-gray-900">{tab.label}</p>
                  {activeTab === tab.id && (
                    <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed font-medium">
                      {tab.id === 'overview' && 'A single percentage breaking down verbatim, mosaic, AI, and self-plagiarism.'}
                      {tab.id === 'sources' && 'Every matching source listed with journal, year, and percentage contribution.'}
                      {tab.id === 'ai' && 'AI-flagged sections shown with confidence scores alongside human-written confirmations.'}
                      {tab.id === 'highlight' && 'Every flagged sentence highlighted in context verbatim red, AI purple.'}
                    </p>
                  )}
                </button>
              ))}
            </div>
            <div className="md:col-span-3 bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm min-h-64">
              {reportPanels[activeTab]}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY MANNA PLAG ── */}
      {/* <section className="bg-[#f0f7ff] border-b border-blue-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-2">Why Manna Plagiarism</p>
            <h2 className="text-[24px] font-black text-gray-900 mb-2 tracking-tight">Built differently for people who care about research integrity</h2>
            <p className="text-[14px] text-gray-500 max-w-md mx-auto font-medium">Not a generic tool retrofitted for academia. Every decision was made with publishers, editors, and researchers in mind.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {whyCards.map(c => (
              <div key={c.title} className="bg-white border border-blue-100 rounded-md p-5">
                <div className="w-9 h-9 rounded-md flex items-center justify-center mb-3 text-lg" style={{ background: c.tagBg }}>{c.emoji}</div>
                <p className="text-[13px] font-bold text-gray-900 mb-2">{c.title}</p>
                <p className="text-[11px] text-gray-500 leading-relaxed mb-3 font-medium">{c.desc}</p>
                <span className="text-[10px] px-2 py-1 rounded-full font-bold" style={{ background: c.tagBg, color: c.tagTxt }}>{c.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ── CLIENT LIST ── */}
      <section className="bg-white border-b border-gray-100 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-[22px] font-black text-gray-800 text-center mb-2 tracking-tight">
            Partnering with academic institutions and publishers
          </h2>
          <p className="text-[13px] text-gray-400 text-center font-medium mb-10">
            From research universities to journal publishers across disciplines and borders
          </p>

          {/* Symmetrical 4x2 grid of verified clients with logos */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {clients.map(c => (
              <div
                key={c.name}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all p-4 sm:p-5 flex flex-col items-center justify-between text-center h-36 group relative"
                title={`${c.name} — ${c.sub}`}
              >
                <div className="h-20 w-full flex items-center justify-center p-1">
                  <img
                    src={c.logo}
                    alt={c.name}
                    className="max-h-16 w-auto max-w-[90%] object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="w-full">
                  <div className="text-[12px] font-bold text-gray-800 truncate px-1" title={c.name}>
                    {c.name}
                  </div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold truncate px-1 mt-0.5">
                    {c.sub}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="text-[24px] font-black text-gray-900 tracking-tight">Integrity matters most to those who build the future</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map(t => (
              <div key={t.name} className="bg-white border border-gray-100 rounded-md p-6 flex flex-col">
                <div className="text-2xl mb-3">💬</div>
                <p className="text-[13px] text-gray-700 leading-relaxed flex-1 mb-5 font-medium">{t.quote}</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-black text-white flex-shrink-0 shadow-sm" style={{ background: t.bg }}>{t.initials}</div>
                  <div>
                    <p className="text-[12px] font-bold text-gray-900">{t.name}</p>
                    <p className="text-[11px] text-gray-400 font-medium">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT STRIP ── */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex gap-5 items-start bg-gray-50 rounded-lg p-6 border border-gray-100">
            <div className="w-10 h-10 rounded-md bg-navy flex items-center justify-center flex-shrink-0 shadow-sm">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 21h18M9 21V7l9-4v18" />
              </svg>
            </div>
            <div>
              <p className="text-[14px] font-bold text-gray-900 mb-2">Built by Manna Analytics Private Limited</p>
              <p className="text-[13px] text-gray-500 leading-relaxed max-w-2xl font-medium">
                For over a decade, we have been building AI-driven ERP systems and manuscript management platforms for the publishing industry. MannaPlag is built on the same foundation deep domain expertise, real publishing workflows, and a commitment to academic integrity.
              </p>
              <a href="https://mannaanalytics.com/about" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-[13px] font-semibold text-blue-600 hover:underline">
                Learn more about us
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOMEPAGE FAQS ── */}
      <section className="bg-gray-50 border-b border-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[11px] font-bold text-blue-600 uppercase tracking-widest mb-2">Got Questions?</p>
            <h2 className="text-[28px] font-black text-gray-900 tracking-tight mb-3">
              Frequently asked questions
            </h2>
            <p className="text-[14px] text-gray-500 font-medium max-w-lg mx-auto">
              Everything you need to know about MannaPlag's plagiarism detection, AI screening, and data confidentiality.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {homeFaqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={faq.q}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden transition-all shadow-sm"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 font-bold text-[15px] text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    <span>{faq.q}</span>
                    <span className="text-gray-400 text-lg flex-shrink-0 font-mono">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 pt-1 text-[13px] text-gray-600 leading-relaxed font-medium border-t border-gray-50">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/faqs"
              className="inline-flex items-center gap-1.5 text-[13px] font-bold text-navy hover:text-blue-600 transition-colors"
            >
              Have more questions? View all FAQs →
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="text-[28px] font-black text-white mb-3 tracking-tight">Ready to check your manuscript?</h2>
          <p className="text-[14px] text-white/55 mb-8 max-w-md mx-auto font-medium">Start with 3 free checks. No credit card required.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/register" className="px-6 py-3 bg-white text-navy text-[14px] font-bold rounded-md hover:bg-blue-50 transition-colors shadow-md">
              Get started free
            </Link>
            <Link to="/pricing" className="px-6 py-3 border border-white/25 text-white text-[14px] font-semibold rounded-md hover:bg-white/10 transition-colors">
              View pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
